import { type FC, useMemo } from 'react'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { useAuthenticatedUser } from '../../../../common/useAuthenticatedUser'
import { isValidEmail } from '../../../../common/util'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { type BookingClient } from '../../../../models/Scheduling'
import { clientDisplayName } from '../../shared/schedulingDisplay'
import { ClientEmailNotice } from '../ClientEmailNotice'
import { hasClientEmail } from '../clientEmailGaps'
import { useContactSearch } from '../../../../common/useContactSearch'
import { SchedulingDropdownItem } from './SchedulingDropdownItem'
import { dropdownMessage, dropdownSectionHeader } from '../../../../components/SelectDropdown/dropdownOptions'

const _NO_MATCH_MESSAGE = 'No matches. Type a full email to add a guest.'

const _EMAIL_OPTION_PREFIX = 'email:'

interface ClientSearchProps {
  clients: BookingClient[]
  ownerId?: string
  onClientsChange: (clients: BookingClient[]) => void
}

const clientKey = (client: BookingClient): string => client.contactId ?? client.email ?? ''

const splitName = (name: string): { firstName?: string; lastName?: string } => {
  const [firstName, ...rest] = name.trim().split(/\s+/)
  return { firstName, lastName: rest.length > 0 ? rest.join(' ') : undefined }
}

const emailInviteOption = (email: string): SelectOption => ({
  id: `${_EMAIL_OPTION_PREFIX}${email}`,
  label: email,
  displayLabel: (
    <span className='d-flex flex-column'>
      <span>{email}</span>
      <span className='paragraph-small zocks-text-gray-500'>Add as a guest</span>
    </span>
  ),
})

export const ClientSearch: FC<ClientSearchProps> = ({ clients, ownerId, onClientsChange }) => {
  const accountId = useAuthenticatedUser().accountId
  const { data: contacts, search, setSearch, isFetching } = useContactSearch({ ownerId, accountId })

  const options = useMemo(() => {
    const term = search?.trim() ?? ''
    const userOptions: SelectOption[] = contacts
      .filter((user) => !clients.some((client) => client.contactId === user.id))
      .map((user) => ({
        id: user.id,
        label: getUserDisplayName(user),
        displayLabel: (
          <span>
            {getUserDisplayName(user)} <span className='zocks-text-gray-500'>· {user.type || 'Client'}</span>
          </span>
        ),
      }))
    const matchesKnownEmail =
      contacts.some((user) => user.email?.toLowerCase() === term.toLowerCase()) || clients.some((client) => client.email?.toLowerCase() === term.toLowerCase())
    const emailOption: SelectOption[] = isValidEmail(term) && !matchesKnownEmail ? [emailInviteOption(term)] : []
    const sections: SelectOption[] = []
    if (userOptions.length > 0) {
      sections.push(dropdownSectionHeader('contacts', 'Contacts'), ...userOptions)
    }
    if (emailOption.length > 0) {
      sections.push(dropdownSectionHeader('email', 'Add by email'), ...emailOption)
    }
    return sections.length > 0 ? sections : [dropdownMessage(_NO_MATCH_MESSAGE)]
  }, [contacts, clients, search])

  const addClient = (client: BookingClient) => {
    onClientsChange([...clients, client])
    setSearch('')
  }

  const addEmailInvitee = (email: string) => {
    const trimmed = email.trim()
    if (!isValidEmail(trimmed) || clients.some((client) => client.email === trimmed)) {
      return
    }
    addClient({ email: trimmed })
  }

  const handleOptionSelected = (option: SelectOption) => {
    if (option.id.startsWith(_EMAIL_OPTION_PREFIX)) {
      addEmailInvitee(option.id.slice(_EMAIL_OPTION_PREFIX.length))
      return
    }
    const user = contacts.find((contact) => contact.id === option.id)
    addClient({ contactId: option.id, ...splitName(user ? getUserDisplayName(user) : option.label), email: user?.email ?? undefined })
  }

  const removeClient = (key: string) => {
    onClientsChange(clients.filter((client) => clientKey(client) !== key))
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.25'>
      <label className='bold'>Clients</label>
      <div className='paragraph-small zocks-text-gray-600'>Who the meeting is for. Add a couple or business partners together.</div>
      <SelectDropdown
        options={options}
        // A pure search box: never adopt the first result as a selection (the default is options[0],
        // which would overwrite the typed term with the first match), and clear after each pick.
        initialSelectedOption={null}
        placeholder='Search by name or write email address'
        searchable
        loading={isFetching}
        clearDropdownOnOptionSelected
        inputAutoComplete='off'
        DropDownItemOptionComponent={SchedulingDropdownItem}
        onSearchInputChange={async (term) => setSearch(term)}
        onSearchInputSelected={addEmailInvitee}
        onOptionSelected={handleOptionSelected}
      />
      <ClientEmailNotice clients={clients} />
      {clients.length > 0 ? (
        <div className='d-flex flex-wrap zocks-gap-0.5 mt-2'>
          {clients.map((client, index) => {
            const key = clientKey(client)
            return (
              <span
                key={key || index}
                className='d-inline-flex align-items-center zocks-gap-0.25 badge rounded-pill zocks-bg-color-gray-200 zocks-text-gray-600'
              >
                {clientDisplayName(client)}
                {client.contactId && hasClientEmail(client) ? (
                  <span className='zocks-text-gray-500'>· Contact</span>
                ) : (
                  <span className='zocks-text-gray-500'>· Guest</span>
                )}
                <button
                  type='button'
                  aria-label={`Remove ${clientDisplayName(client)}`}
                  className='btn btn-link p-0 border-0 d-inline-flex align-items-center zocks-text-gray-600'
                  onClick={() => removeClient(key)}
                >
                  <i className='bi bi-x' aria-hidden='true' />
                </button>
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
